if (!customElements.get('product-bundle-builder')) {
  customElements.define(
    'product-bundle-builder',
    class ProductBundleBuilder extends HTMLElement {
      connectedCallback() {
        this.priceEl = this.querySelector('[data-bundle-total-price]');
        this.compareEl = this.querySelector('[data-bundle-total-compare]');
        this.saveBadgeEl = this.querySelector('[data-bundle-save-badge]');
        this.priceBar = this.querySelector('.bundle-builder__price-bar');
        this.variantIdInput = this.querySelector('.product-variant-id');
        this.productForm = this.querySelector('product-form');
        this.moneyFormat =
          this.closest('.bundle-builder-section')?.dataset.moneyFormat ||
          document.querySelector('.bundle-builder-section')?.dataset.moneyFormat ||
          '${{amount}}';

        if (!this._clickBound) {
          this._clickBound = true;
          this.addEventListener('click', this.onClick.bind(this));
        }

        this.initBenefitsAccordions();
        this.refreshBundleState();

        const submitButton = this.productForm?.querySelector('[type="submit"]');
        submitButton?.removeAttribute('aria-disabled');
      }

      onClick(event) {
        if (event.target.closest('.bundle-builder__benefits-summary')) return;

        const option = event.target.closest('.bundle-builder__option');
        if (!option || !this.contains(option)) return;

        const step = option.closest('.bundle-builder__step');
        if (!step) return;

        step.querySelectorAll('.bundle-builder__option').forEach((button) => {
          const isSelected = button === option;
          button.classList.toggle('is-selected', isSelected);
          button.setAttribute('aria-selected', isSelected ? 'true' : 'false');
        });

        this.refreshBundleState();
      }

      initBenefitsAccordions() {
        this.querySelectorAll('.bundle-builder__benefits-details').forEach((details) => {
          const summary = details.querySelector('summary');
          if (!summary || summary.dataset.benefitsBound === 'true') return;

          summary.dataset.benefitsBound = 'true';
          summary.setAttribute('role', 'button');
          summary.setAttribute('aria-expanded', details.hasAttribute('open') ? 'true' : 'false');
          summary.setAttribute('aria-controls', details.querySelector('.bundle-builder__benefits-panel')?.id || '');

          summary.addEventListener('click', (event) => {
            event.preventDefault();

            const isOpen = details.hasAttribute('open');
            if (isOpen) {
              details.removeAttribute('open');
            } else {
              details.setAttribute('open', '');
            }

            summary.setAttribute('aria-expanded', isOpen ? 'false' : 'true');
          });
        });
      }

      getSteps() {
        return Array.from(this.querySelectorAll('.bundle-builder__step'));
      }

      getStepSelections() {
        return this.getSteps().map((step) => {
          const selected = step.querySelector('.bundle-builder__option.is-selected');
          return {
            step,
            mode: step.dataset.selectionMode || 'product',
            selected,
            variantId: selected?.dataset.variantId || '',
            optionTitle: selected?.dataset.optionTitle || selected?.querySelector('.bundle-builder__option-title')?.textContent?.trim() || '',
            stepTitle: step.querySelector('.bundle-builder__step-title')?.textContent?.trim() || '',
            price: Number(selected?.dataset.price || 0),
            comparePrice: Number(selected?.dataset.comparePrice || 0),
          };
        });
      }

      getBundleVariantIds() {
        return this.getStepSelections()
          .map((selection) => selection.variantId)
          .filter(Boolean);
      }

      getBundleValidationError() {
        const steps = this.getSteps();
        if (!steps.length) return null;

        const selections = this.getStepSelections();
        const missingSelection = selections.find((selection) => !selection.selected);
        if (missingSelection) {
          return 'Please choose one option from each step.';
        }

        const missingVariant = selections.find((selection) => selection.selected && !selection.variantId);
        if (missingVariant) {
          const label = missingVariant.optionTitle || missingVariant.stepTitle || 'One step';
          if (missingVariant.mode === 'variant') {
            return `${label} could not be matched to a variant. Check the variant ID or source product in the theme editor.`;
          }
          return `${label} could not be loaded. Re-select the product in the theme editor (unlisted products must be assigned directly).`;
        }

        return null;
      }

      isBundleReady() {
        return !this.getBundleValidationError();
      }

      refreshBundleState() {
        this.updatePrices();
      }

      updatePrices() {
        if (!this.priceEl) return;

        let totalPrice = 0;
        let totalCompare = 0;
        let firstVariantId = null;

        this.getStepSelections().forEach((selection) => {
          if (!selection.selected) return;

          if (!firstVariantId && selection.variantId) {
            firstVariantId = selection.variantId;
          }

          if (selection.price > 0) totalPrice += selection.price;
          if (selection.comparePrice > selection.price) {
            totalCompare += selection.comparePrice;
          } else if (selection.price > 0) {
            totalCompare += selection.price;
          }
        });

        this.priceEl.textContent = this.formatMoney(totalPrice);

        if (this.compareEl) {
          if (totalCompare > totalPrice) {
            this.compareEl.textContent = this.formatMoney(totalCompare);
            this.compareEl.classList.remove('hidden');
          } else {
            this.compareEl.textContent = '';
            this.compareEl.classList.add('hidden');
          }
        }

        if (this.saveBadgeEl && this.priceBar) {
          const suffix = this.priceBar.dataset.saveSuffix || 'Today';
          if (totalCompare > totalPrice && totalCompare > 0) {
            const percent = Math.round(((totalCompare - totalPrice) / totalCompare) * 100);
            this.saveBadgeEl.textContent = `Save ${percent}% ${suffix}`.trim();
            this.saveBadgeEl.classList.remove('hidden');
          } else {
            this.saveBadgeEl.textContent = '';
            this.saveBadgeEl.classList.add('hidden');
          }
        }

        if (this.variantIdInput) {
          this.variantIdInput.value = firstVariantId || '';
          this.variantIdInput.disabled = !firstVariantId;
        }
      }

      formatMoney(cents) {
        const formatString = this.moneyFormat || '${{amount}}';
        const placeholderRegex = /\{\{\s*(\w+)\s*\}\}/;

        const formatWithDelimiters = (number, precision = 2, thousands = ',', decimal = '.') => {
          const fixed = (number / 100).toFixed(precision);
          const parts = fixed.split('.');
          const dollars = parts[0].replace(/(\d)(?=(\d\d\d)+(?!\d))/g, `$1${thousands}`);
          const centsPart = parts[1] ? decimal + parts[1] : '';
          return dollars + centsPart;
        };

        const match = formatString.match(placeholderRegex);
        if (!match) return formatWithDelimiters(cents, 2);

        switch (match[1]) {
          case 'amount_no_decimals':
            return formatString.replace(placeholderRegex, formatWithDelimiters(cents, 0));
          case 'amount_with_comma_separator':
            return formatString.replace(placeholderRegex, formatWithDelimiters(cents, 2, '.', ','));
          case 'amount_no_decimals_with_comma_separator':
            return formatString.replace(placeholderRegex, formatWithDelimiters(cents, 0, '.', ','));
          default:
            return formatString.replace(placeholderRegex, formatWithDelimiters(cents, 2));
        }
      }
    }
  );

  document.addEventListener('shopify:section:load', (event) => {
    event.target.querySelectorAll('product-bundle-builder').forEach((builder) => {
      builder.initBenefitsAccordions();
      builder.refreshBundleState();
    });
  });
}
